import { useCallback, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import { GlobalSearch } from "../GlobalSearch";
import { CommandPalette } from "../CommandPalette";
import { useSelector } from "react-redux";
import { RootReduxState } from "~/modules/app/components/ReduxProvider/types";
import { useUser } from "~/modules/user/hooks/useUser";
import { removeFeatureFlaggedResults } from "../../util/removeFeatureFlaggedResults";
import { useRecentSearches } from "../../hooks/useRecentSearches";
import { SearchResultItem } from "../../types";
import { toRecentSearchItem } from "../../util/recentSearches";
import * as Analytics from "../../analytics";

// // // //

/**
 * Layout for the GlobalSearch feature
 * Fetches search results from the API and passes them into GlobalSearch + CommandPalette components
 */
export function SearchLayout(props: { isMobile?: boolean }) {
    const { isMobile = false } = props;
    const { recentSearches, addRecentSearch, clearRecentSearches } =
        useRecentSearches();

    // Get state from search Redux store
    const { user } = useUser();
    const { groups, loading } = useSelector(
        (state: RootReduxState) => state.search,
    );

    // Get the filtered groups based on the user's feature flags
    const filteredGroups = removeFeatureFlaggedResults({
        groups,
        featureFlagState: user.features,
    });

    const debouncedTrackQuery = useMemo(
        () =>
            debounce((query: string) => {
                if (query !== "") {
                    Analytics.trackSearchQuery({ query });
                }
            }, 1500),
        [],
    );

    useEffect(() => {
        return () => {
            debouncedTrackQuery.cancel();
        };
    }, [debouncedTrackQuery]);

    const handleQueryChange = useCallback(
        (query: string) => {
            debouncedTrackQuery(query);
        },
        [debouncedTrackQuery],
    );

    const handleSelectSearchResult = (result: SearchResultItem) => {
        addRecentSearch(toRecentSearchItem(result));
        Analytics.trackRecentSearchAdd({
            label: result.content.label,
            href: result.href,
        });
    };

    const handleClearRecentSearches = () => {
        clearRecentSearches();
        Analytics.trackRecentSearchClear();
    };

    return (
        <>
            <GlobalSearch
                groups={filteredGroups}
                loadingSearch={loading}
                recentSearches={recentSearches}
                onSelectSearchResult={handleSelectSearchResult}
                onClearRecentSearches={handleClearRecentSearches}
                onQueryChange={handleQueryChange}
            />
            {isMobile === false && (
                <CommandPalette
                    groups={filteredGroups}
                    loadingSearch={loading}
                    recentSearches={recentSearches}
                    onSelectSearchResult={handleSelectSearchResult}
                    onClearRecentSearches={handleClearRecentSearches}
                    onQueryChange={handleQueryChange}
                />
            )}
        </>
    );
}
